import * as React from 'react'

import getConfig from 'next/config'

import { Product as SdProduct } from '@grandvision/analytics'
import { Products as ProductsApi } from '@grandvision/api-client'
import {
  CmsComponentSlot,
  CmsContainerPage,
  CmsHeadTags,
  CmsPageLayout,
  CmsProductBreadcrumbs,
  useBloomreachPageRoutes,
  useBloomreachPdpContent,
  useBloomreachProductRoutes,
} from '@grandvision/bloomreach'
import {
  AddToCart as AddToCartV2,
  Context as CartContext,
  Hooks,
  MiniCartModal,
  Upsell,
} from '@grandvision/carts-v2'
import { CommerceToolsApi } from '@grandvision/commercetools'
import { OlapicProvider } from '@grandvision/common'
import { withErrorPage } from '@grandvision/next'
import {
  getProductSeoInfo,
  ProductDetail,
  ProductDetailProvider,
  ProductFrameMeasurements,
  ProductTypes,
  RenderAddToCartButton,
  VtoConfigProvider,
} from '@grandvision/products'
import { ReviewDisplay, ReviewsProvider } from '@grandvision/reviews'
import { CrossSellProducts } from '@grandvision/search'
import { useIntl } from '@grandvision/state'
import { getAttrValue, isString } from '@grandvision/utils'

import { defaultErrorProps, ErrorProps } from '../../components/error-props'
import OpticianForm from '../../components/forms/Optician'
import { layoutProps } from '../../components/layout-props'
import { getAlgoliaCrossSellConfig } from '../../util/algolia'
import { getCartSettings } from '../../util/cart-settings'
import { deliveryResolverFactory } from '../../util/delivery-resolver-factory'
import {
  isCartV2Enabled,
  isContactLensEnabled,
  isContactLensStockEnabled,
  isCrossSellEnabled,
  isPrescriptionFileEnabled,
  isPrescriptionGlassesConfiguratorValidationStepEnabled,
  isPrescriptionGlassesEnabled,
  isReviewsEnabled,
  isSolutionsEnabled,
  isVtoEnabled,
} from '../../util/feature-toggle'
import { stockResolverFactory } from '../../util/stock-resolver-factory'

const { publicRuntimeConfig } = getConfig()
const endpoints = publicRuntimeConfig?.grandvision?.endpoints || {}
const apiKey = publicRuntimeConfig?.grandvision?.apiKey || ''
const defaultLocale = publicRuntimeConfig?.defaultLocale
const staticsUrl = publicRuntimeConfig?.grandvision?.staticsUrl || ''
const selfUrl = publicRuntimeConfig?.selfUrl || ''
const vtoConfig = publicRuntimeConfig?.ditto || {}
const { apiKey: powerReviewsApiKey, groupId, opcoId } = publicRuntimeConfig?.powerReviews || {}

const featureToggles: Record<string, boolean> = Object.entries({
  isSolutionsEnabled,
  isContactLensStockEnabled,
  isPrescriptionGlassesEnabled,
  isPrescriptionFileEnabled,
  isPrescriptionEnabled: isContactLensEnabled,
  isPrescriptionInfoEnabled: () => false,
}).reduce((result, [key, fn]) => ({ ...result, [key]: fn() }), {})

type ReviewProviderProps = React.ComponentProps<typeof ReviewsProvider>

type InitialProps = {
  sku: string
  product?: CommerceToolsApi.ProductProjection
}

type Props = InitialProps & {}

export const ProductDetailPage = ({ sku, product }: Props) => {
  const { locale } = useIntl()
  const content = useBloomreachPdpContent()
  const { routes } = useBloomreachPageRoutes()
  const [, { formatProductUrl }, productRoutes] = useBloomreachProductRoutes()

  /**
   * Product data, seo, stock, delivery, cross-sell configurations
   */
  const productType = product?.productType?.obj?.name as CommerceToolsApi.ProductTypeName
  const productUrl = selfUrl + formatProductUrl({ slug: product?.slug ?? {}, productType, sku })
  const brandKey = String(getAttrValue(product?.masterVariant ?? {}, 'brandKey'))
  const noAddonsImg = content.prescriptionGlasses.noAddonsImage as ProductTypes.BloomreachImage
  const seoInfo = getProductSeoInfo(staticsUrl, locale, product)
  const specifications = React.useMemo(() => getSpecifications(productType), [productType])
  const stockResolver = React.useMemo(() => stockResolverFactory(), [])
  const deliveryResolver = React.useMemo(() => deliveryResolverFactory(), [])
  const crossSellConfig = React.useMemo(() => getAlgoliaCrossSellConfig(productType), [productType])

  /**
   * Product type checks
   */
  const isLenses = React.useCallback(() => CommerceToolsApi.isLenses(productType), [productType])

  /**
   * PowerReviews setup
   */
  const reviewsInitialId = isLenses() ? brandKey : product?.masterVariant.sku
  const reviewsInitialSku = isLenses() ? undefined : sku // SKU is not required for CL
  const reviewsProviderProps = React.useMemo<ReviewProviderProps>(
    () => ({
      enabled: isReviewsEnabled() && !!product?.reviewEnabled,
      config: {
        groupId,
        opcoId,
        apiKey: powerReviewsApiKey,
        writeReviewPath: routes['write-review'],
      },
    }),
    // other fields are static during runtime
    [product, routes]
  )

  /**
   * Cart v2 setup (optional)
   */
  const renderAddToCartButton: RenderAddToCartButton | undefined = isCartV2Enabled()
    ? ({ caption, lineItemDrafts, onSuccess }) => (
        <AddToCartV2 caption={caption} onSuccess={onSuccess} lineItemDrafts={lineItemDrafts} />
      )
    : undefined

  if (!product) {
    console.error(`Failed to render ProductDetailPage because of invalid product data: ${product}`)
    return null
  }

  /**
   * Cart v2 upsell setup (optional)
   */
  const productCatKey = getAttrValue(product.masterVariant, 'recommendedProductCatKey')
  const recommendedCategoryKey = isString(productCatKey) ? productCatKey : undefined

  const subscriptionSku = getAttrValue(product.masterVariant, 'subscriptionProductSKU')
  const subscriptionProductSku = isString(subscriptionSku) ? subscriptionSku : undefined

  return (
    <CmsContainerPage>
      <CmsHeadTags
        title={seoInfo.title}
        description={seoInfo.description}
        ogImage={seoInfo.imageUrl}
        ogUrl={productUrl}
      />
      <CmsPageLayout {...layoutProps}>
        <div className="product-detail-layout">
          <CmsComponentSlot path="main/top-container" />
          <CartContext.CartSettingsProvider
            settings={{
              ...getCartSettings(formatProductUrl, productRoutes),
              readOnly: true,
            }}
          >
            <ProductDetailProvider
              product={product}
              sku={sku}
              productType={productType}
              locale={locale}
              staticsUrl={staticsUrl}
              isPrescriptionGlassesConfiguratorValidationStepEnabled={isPrescriptionGlassesConfiguratorValidationStepEnabled()}
              renderAddToCartButton={renderAddToCartButton}
              useAddToCart={isCartV2Enabled() ? Hooks.useAddToCart : undefined}
            >
              <CmsProductBreadcrumbs product={product} sku={sku} />
              <ReviewsProvider {...reviewsProviderProps}>
                <VtoConfigProvider enabled={isVtoEnabled()} config={vtoConfig}>
                  <ProductDetail
                    routes={routes}
                    productType={productType}
                    specificationConfiguration={specifications}
                    deliveryResolver={deliveryResolver}
                    deliveryResolverFactory={deliveryResolverFactory}
                    maxOrderableQuantity={5}
                    stockResolver={stockResolver}
                    productTypeUsps={content.usps}
                    opticianForm={OpticianForm}
                    formatProductUrl={formatProductUrl}
                    allowBackOrder={true}
                    noAddOnsImage={noAddonsImg}
                    {...featureToggles}
                  />
                </VtoConfigProvider>

                {!isLenses() && <ProductFrameMeasurements product={product} />}

                {isCrossSellEnabled() && (
                  <CrossSellProducts
                    algoliaConfig={crossSellConfig}
                    formatProductUrl={formatProductUrl}
                  />
                )}

                {isReviewsEnabled() && (
                  <ReviewDisplay productId={reviewsInitialId} variantId={reviewsInitialSku} />
                )}

                <div className="product-detail-layout__components">
                  <OlapicProvider sku={sku}>
                    <CmsComponentSlot path="main/bottom-container" />
                  </OlapicProvider>
                </div>

                {isCartV2Enabled() ? (
                  <MiniCartModal links={{ cart: routes.cart, checkout: routes.checkout }}>
                    {recommendedCategoryKey || subscriptionProductSku
                      ? (addedLineItems) => (
                          <Upsell
                            recommendedCategoryKey={recommendedCategoryKey}
                            subscriptionProductSku={subscriptionProductSku}
                            lineItems={addedLineItems}
                          />
                        )
                      : null}
                  </MiniCartModal>
                ) : null}

                <SdProduct
                  product={product}
                  activeSKU={sku}
                  locale={locale}
                  staticsUrl={staticsUrl}
                  url={productUrl}
                />
              </ReviewsProvider>
            </ProductDetailProvider>
          </CartContext.CartSettingsProvider>
        </div>
      </CmsPageLayout>
    </CmsContainerPage>
  )
}

ProductDetailPage.getInitialProps = async ({ req }): Promise<InitialProps | ErrorProps> => {
  const path = req.originalUrl
  const [, slug, sku] = path.match(/.+\/(.+)\/([^\?]+)(\?.*)?$/)
  if (!sku || !slug) {
    throw new Error(
      `Can't extract slug and sku from ${path}, got sku: '${String(sku)}', slug: '${String(slug)}'`
    )
  }

  const locale = req.locale?.toString() || defaultLocale

  let product: CommerceToolsApi.ProductProjection | undefined
  try {
    product = await ProductsApi.getProductBySlug(endpoints.products, { slug }, apiKey)
  } catch (err) {
    // Non OK product service api response, return props to show the error page
    console.error(`Failed to fetch product with slug: ${String(slug)}`, err)
    return {
      ...defaultErrorProps[locale],
      statusCode: err.statusCode,
    }
  }

  return {
    sku,
    product,
  }
}

type Specification = {
  labelKey: string
  attribute: string
  isLocalised?: boolean
  isSet?: boolean
}

function getSpecifications(productType: string): Specification[] | undefined {
  if (productType === 'Sunglasses' || productType === 'Frames') {
    return [
      {
        labelKey: 'products.specifications.lensColor',
        attribute: 'lensColor',
        isLocalised: true,
      },
      {
        labelKey: 'products.specifications.frameColor',
        attribute: 'frameColor',
        isLocalised: true,
      },
      {
        labelKey: 'products.specifications.frameMaterial',
        attribute: 'frameMaterial',
        isLocalised: true,
      },
      {
        labelKey: 'products.specifications.frameShape',
        attribute: 'frameShape',
        isLocalised: true,
      },
      {
        labelKey: 'products.specifications.frameType',
        attribute: 'frameType',
        isLocalised: true,
      },
      {
        labelKey: 'products.specifications.gender',
        attribute: 'gender',
        isLocalised: true,
        isSet: true,
      },
    ]
  }
}

export default withErrorPage(ProductDetailPage)
